import type {ReminderWeekday} from '../domain/reminder-weekday.value-object';
import type {Result} from 'framework';
import type {AcknowledgeReminder} from '../application/use-cases/acknowledge-reminder';
import type {DeleteAvatar} from '../application/use-cases/delete-avatar';
import type {GetProfile} from '../application/use-cases/get-profile';
import type {ListDueReminders} from '../application/use-cases/list-due-reminders';
import type {ListReminders} from '../application/use-cases/list-reminders';
import type {UpdateAvatar} from '../application/use-cases/update-avatar';
import type {UpdateBodyMetrics} from '../application/use-cases/update-body-metrics';
import type {UpdateReminder} from '../application/use-cases/update-reminder';
import type {SettingsError} from '../domain/errors';
import type {UserProfile, WritableBodyMetrics} from '../domain/user-profile';
import type {ReminderScheduleInterval, ReminderKind, UserReminder} from '../domain/user-reminder';

export class SettingsController {
  constructor(
    private readonly getProfileUseCase: GetProfile,
    private readonly updateBodyMetricsUseCase: UpdateBodyMetrics,
    private readonly updateAvatarUseCase: UpdateAvatar,
    private readonly deleteAvatarUseCase: DeleteAvatar,
    private readonly listRemindersUseCase: ListReminders,
    private readonly listDueRemindersUseCase: ListDueReminders,
    private readonly updateReminderUseCase: UpdateReminder,
    private readonly acknowledgeReminderUseCase: AcknowledgeReminder,
  ) {}

  getProfile(): Promise<Result<UserProfile, SettingsError>> {
    return this.getProfileUseCase.execute();
  }

  updateBodyMetrics(metrics: WritableBodyMetrics): Promise<Result<UserProfile, SettingsError>> {
    return this.updateBodyMetricsUseCase.execute(metrics);
  }

  updateAvatar(file: File): Promise<Result<UserProfile, SettingsError>> {
    return this.updateAvatarUseCase.execute(file);
  }

  deleteAvatar(): Promise<Result<UserProfile, SettingsError>> {
    return this.deleteAvatarUseCase.execute();
  }

  listReminders(): Promise<Result<readonly UserReminder[], SettingsError>> {
    return this.listRemindersUseCase.execute();
  }

  listDueReminders(): Promise<Result<readonly UserReminder[], SettingsError>> {
    return this.listDueRemindersUseCase.execute();
  }

  updateReminder(
    kind: ReminderKind,
    patch: {
      readonly interval?: ReminderScheduleInterval;
      readonly weekdays?: readonly ReminderWeekday[];
      readonly enabled?: boolean;
    },
  ): Promise<Result<UserReminder, SettingsError>> {
    return this.updateReminderUseCase.execute({kind, ...patch});
  }

  acknowledgeReminder(kind: ReminderKind): Promise<Result<UserReminder, SettingsError>> {
    return this.acknowledgeReminderUseCase.execute({kind});
  }
}
